import trackMixpanelEvent from '@/common/utils/mixpanel';
import Underline from '@tiptap/extension-underline';
import { Focus, Placeholder } from '@tiptap/extensions';
import { useEditor } from '@tiptap/react';
import { debounce } from 'lodash';
import React, { useEffect, useRef } from 'react';
import LessonGuidanceBlock from '../TipTapCustomExtensions/LessonGuidanceBlockExtension';
import { sharedEditorExtensions } from '../TipTapCustomExtensions/sharedEditorExtensions';
import BlockHandle from './ColumnBlockHandle';
import {
  EditorContainer,
  EditorWrapper,
  StyledEditorContent,
} from './editorStyles';
import FixedToolbar from './FixedToolbar';
import { exportToPpt } from './lessonPlanPptExporter';
import { parseHTMLToSections } from './utils';

interface LessonPlanEditorProps {
  content: string;
  onContentChange: (content: string) => void;
  selectedSectionId: string | null;
  onSelectedSectionIdChange: (id: string | null) => void;
}

export default function LessonPlanEditor({
  content,
  onContentChange,
  selectedSectionId,
  onSelectedSectionIdChange,
}: LessonPlanEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    trackMixpanelEvent('Print lesson plan');
    if (!editorRef.current) return;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.top = '-10000px';
    iframe.style.left = '-10000px';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
      document.body.removeChild(iframe);
      return;
    }

    // Copy stylesheets so editor content renders correctly
    const stylesheets = Array.from(
      document.querySelectorAll('style, link[rel="stylesheet"]'),
    );
    const stylesHTML = stylesheets.map((s) => s.outerHTML).join('\n');

    doc.open();
    doc.write(`
      <html>
        <head>
          ${stylesHTML}
          <style>@page { margin: 1.5cm; }</style>
        </head>
        <body>${editorRef.current.outerHTML}</body>
      </html>
    `);
    doc.close();

    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();

    // Clean up after a short delay to allow print dialog to open
    setTimeout(() => document.body.removeChild(iframe), 1000);
  };

  const handleSectionClick = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    const activityBlock = target.closest('.activity-block, h3[data-id]');
    if (activityBlock) {
      const sectionId = activityBlock.getAttribute('data-id');
      if (sectionId) {
        onSelectedSectionIdChange(sectionId);
      }
    }
  };

  const editor = useEditor({
    extensions: [
      ...sharedEditorExtensions,
      Underline,
      Focus.configure({
        className: 'has-focus',
        mode: 'all',
      }),
      LessonGuidanceBlock,
      Placeholder.configure({
        placeholder:
          'Write something or use the control panel to add a new activity',
      }),
    ],
    content: '',
  });

  const handleExportToPpt = () => {
    trackMixpanelEvent('Export lesson plan ppt');
    const sections = parseHTMLToSections(content);
    exportToPpt(sections);
  };

  useEffect(() => {
    if (editor && content) {
      // Only set content if it's different from the current editor content
      if (editor.getHTML() !== content) {
        editor.commands.setContent(content);
      }
    }
  }, [editor, content]);

  useEffect(() => {
    // Create a debounced version of onContentChange
    const debouncedUpdateContent = debounce((newContent: string) => {
      onContentChange(newContent);
    }, 300);

    if (editor) {
      // Create a function to handle content updates
      const handleContentUpdate = () => {
        const newContent = editor.getHTML();
        if (newContent !== content) {
          debouncedUpdateContent(newContent);
        }
      };

      // Listen to both update and selectionUpdate events
      editor.on('update', handleContentUpdate);
      editor.on('selectionUpdate', handleContentUpdate);

      // Also listen to transaction events which capture deletions
      editor.on('transaction', ({ transaction }) => {
        if (transaction.docChanged) {
          handleContentUpdate();
        }
      });

      return () => {
        editor.off('update', handleContentUpdate);
        editor.off('selectionUpdate', handleContentUpdate);
        editor.off('transaction');
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  useEffect(() => {
    if (editor && selectedSectionId) {
      const element = editor.view.dom.querySelector(
        `[data-id="${selectedSectionId}"]`,
      );
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [editor, selectedSectionId]);

  if (!editor) {
    return null;
  }

  return (
    <EditorContainer>
      <FixedToolbar
        editor={editor}
        onExportToPdf={handlePrint}
        onExportToPpt={handleExportToPpt}
      />
      <BlockHandle editor={editor} />
      <EditorWrapper className="EditorWrapper">
        <StyledEditorContent
          editor={editor}
          onClick={handleSectionClick}
          ref={editorRef}
        />
      </EditorWrapper>
    </EditorContainer>
  );
}
